import React from "react";
import { Mail, CheckCircle, HelpCircle } from "lucide-react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import { getBreadcrumbSchema } from "../utils/schema";
import "./Pricing.css";

export const Pricing = () => {
  const breadcrumbSchema = getBreadcrumbSchema([
    { name: 'Home', url: '/' },
    { name: 'Pricing', url: '/pricing' }
  ]);

  const pricingFeatures = [
    "Fully qualified FCCA & MAAT accountant",
    "15+ years of UK practice experience",
    "100% HMRC compliance guarantee",
    "Fixed fee transparency - no hidden costs",
    "Same-day response to queries",
    "Year-round support and advice",
    "Cloud accounting integration",
    "Dedicated accountant relationship",
  ];

  const serviceCategories = [
    {
      title: "Sole Traders & Freelancers",
      services: [
        "Self Assessment tax returns",
        "Bookkeeping and accounts",
        "VAT returns (if registered)",
        "Tax planning advice",
      ],
      note: "Pricing based on turnover and complexity",
    },
    {
      title: "Limited Companies",
      services: [
        "Annual accounts preparation",
        "Corporation tax returns (CT600)",
        "Confirmation statements",
        "Director tax returns",
        "Management accounts",
      ],
      note: "Pricing based on company size and transaction volume",
    },
    {
      title: "Landlords & Property",
      services: [
        "Property income tax returns",
        "Capital gains tax calculations",
        "Portfolio tax planning",
        "Expense optimization advice",
      ],
      note: "Pricing based on number of properties and income level",
    },
    {
      title: "Additional Services",
      services: [
        "Payroll (per employee, per month)",
        "VAT returns (per quarter)",
        "HMRC enquiry support",
        "Business start-up packages",
      ],
      note: "Customized pricing based on specific requirements",
    },
  ];

  const faqs = [
    {
      question: "How do you determine pricing?",
      answer:
        "We provide customized quotes based on your specific circumstances, including business size, transaction volume, complexity, and services required. This ensures you only pay for what you need.",
    },
    {
      question: "Are there any hidden fees?",
      answer:
        "Absolutely not. We provide transparent fixed-fee quotes upfront. The price we quote is the price you pay, with no surprises or hidden charges.",
    },
    {
      question: "Do you offer monthly payment plans?",
      answer:
        "Yes, we offer flexible monthly payment arrangements for ongoing services like bookkeeping, payroll, and annual accounts. This helps spread costs throughout the year.",
    },
    {
      question: "What's included in your fees?",
      answer:
        "Our fees include all professional work, HMRC submissions, ongoing support, and unlimited email queries. Any additional services are discussed and agreed upfront.",
    },
  ];

  return (
    <div className="pricing-page">
      <SEO
        title="Pricing | Accountant Fees Wolverhampton | Transparent Fixed-Fee Accounting"
        description="Transparent, affordable accounting fees for Wolverhampton businesses. Fixed-fee pricing for tax returns, annual accounts, payroll & VAT. No hidden costs. Get a custom quote today."
        canonical="/pricing"
        keywords="accountant fees Wolverhampton, accounting prices Wolverhampton, fixed fee accountant, affordable accountant Wolverhampton"
        schema={breadcrumbSchema}
      />
      {/* Hero Section */}
      <section className="pricing-hero">
        <div className="container">
          <div className="pricing-hero-content">
            <h1>Affordable Accounting Packages for Wolverhampton Businesses</h1>
            <p>
              Transparent, fixed-fee pricing tailored to your specific needs.
              Professional service without the premium agency price tag.
            </p>
          </div>
        </div>
      </section>

      {/* Pricing Approach Section */}
      <section className="section pricing-approach">
        <div className="container">
          <div className="approach-content">
            <h2>Our Pricing Approach</h2>
            <p className="lead-text">
              At Brightson Accounting, we believe in transparent, fair pricing
              based on your actual requirements. Every business is unique, which
              is why we assess your specific needs and provide a customized
              quote that reflects the complexity and scope of work involved.
            </p>
            <div className="pricing-principles">
              <div className="principle-card">
                <CheckCircle size={28} />
                <h4>Case-By-Case Assessment</h4>
                <p>
                  We evaluate your business structure, transaction volume, and
                  service requirements to provide accurate pricing.
                </p>
              </div>
              <div className="principle-card">
                <CheckCircle size={28} />
                <h4>Fixed Fee Quotes</h4>
                <p>
                  Once agreed, your fee is fixed - no hourly rates, no
                  surprises, just clear and predictable costs.
                </p>
              </div>
              <div className="principle-card">
                <CheckCircle size={28} />
                <h4>Value for Money</h4>
                <p>
                  You receive expert qualified service at competitive rates
                  designed for small businesses and individuals.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Categories */}
      <section className="section section-alt service-categories">
        <div className="container">
          <div className="text-center mb-4">
            <h2>Services & Pricing Structure</h2>
            <p className="section-subtitle">
              Overview of our service categories - contact us for a personalized
              quote
            </p>
          </div>
          <div className="grid-2">
            {serviceCategories.map((category, index) => (
              <div key={index} className="category-card">
                <h3>{category.title}</h3>
                <ul className="service-list">
                  {category.services.map((service, idx) => (
                    <li key={idx}>
                      <CheckCircle size={18} />
                      <span>{service}</span>
                    </li>
                  ))}
                </ul>
                <div className="category-note">
                  <HelpCircle size={16} />
                  <span>{category.note}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="section whats-included">
        <div className="container">
          <h2 className="text-center mb-4">What's Included in Every Package</h2>
          <div className="included-grid">
            {pricingFeatures.map((feature, index) => (
              <div key={index} className="included-item">
                <CheckCircle size={20} />
                <span>{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing FAQs */}
      <section className="section section-alt pricing-faqs">
        <div className="container">
          <h2 className="text-center mb-4">Pricing Questions</h2>
          <div className="faq-grid">
            {faqs.map((faq, index) => (
              <div key={index} className="faq-card">
                <h4>{faq.question}</h4>
                <p>{faq.answer}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-4">
            <Link to="/faq" className="btn-secondary">
              View All FAQs
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section pricing-cta">
        <div className="container">
          <div className="cta-content">
            <h2>Get Your Custom Quote Today</h2>
            <p>
              Email us with details of your business and requirements. We'll
              respond within 24 hours with a transparent, fixed-fee quote
              tailored to your needs.
            </p>
            <Link
              to="/contact"
              className="btn-primary btn-large"
              data-testid="pricing-cta-contact"
            >
              <Mail size={22} />
              Get a Quote
            </Link>
            <p className="cta-note">
              No obligation. Clear pricing. Professional service.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
